import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';


@Injectable()
export class EmbedVideoService {

  private readonly validYouTubeOptions = [
    'default',
    'mqdefault',
    'hqdefault',
    'sddefault',
    'maxresdefault'
  ];

  private readonly validVimeoOptions = [
    'thumbnail_small',
    'thumbnail_medium',
    'thumbnail_large'
  ];

  private readonly validDailyMotionOptions = [
    'thumbnail_60_url',
    'thumbnail_120_url',
    'thumbnail_180_url',
    'thumbnail_240_url',
    'thumbnail_360_url',
    'thumbnail_480_url',
    'thumbnail_720_url',
    'thumbnail_1080_url'
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer
  ) { }

  public isVimeoOnline(): Observable<boolean> {
    const vimeoPlayerURL = 'https://player.vimeo.com/api/player.js';
    return this.http
      .get(vimeoPlayerURL, {
        responseType: 'text',
      }).pipe(
        timeout(2000),
        switchMap(() => { return of(true); }),
        catchError((err) => { return of(err.ok); })
      );
  }

  public embed(url: any, options?: any): any {
    let id;
    url = new URL(url);

    id = this.detectYoutube(url);
    if (id) {
      return this.embed_youtube(id, options);
    }

    id = this.detectVimeo(url);
    if (id) {
      return this.embed_vimeo(id, options);
    }

    id = this.detectDailymotion(url);
    if (id) {
      return this.embed_dailymotion(id, options);
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
  public embed_youtube(id: string, options?: any): string {
    options = this.parseOptions(options);

    return this.sanitizeIframe(`<iframe src="https://www.youtube.com/embed/${
      id}${options.query}"${options.attr
    } frameborder="0" allowfullscreen allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"></iframe>`);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
  public embed_vimeo(id: string, options?: any): string {
    options = this.parseOptions(options);
    const separator = options.query === '' ? '?' : '&';
    const queryParams = `${options.query + separator}autopause=0`;
    return this.sanitizeIframe(`<iframe src="https://player.vimeo.com/video/${
      id}${queryParams}"${options.attr
    } frameborder="0" webkitallowfullscreen allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"></iframe>`);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
  public embed_dailymotion(id: string, options?: any): string {
    options = this.parseOptions(options);

    return this.sanitizeIframe(`<iframe src="https://www.dailymotion.com/embed/video/${
      id}${options.query}"${options.attr
    } frameborder="0" allowfullscreen></iframe>`);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
  public embed_image(url: any, options?: any): any {
    let id;

    try {
      url = new URL(url);
    } catch (e) {
      return undefined;
    }

    id = this.detectYoutube(url);
    if (id) {
      return this.embedYouTubeImage(id, options);
    }

    id = this.detectVimeo(url);
    if (id) {
      return this.embedVimeoImage(id, options);
    }

    id = this.detectDailymotion(url);
    if (id) {
      return this.embedDailyMotionImage(id, options);
    }

    return '';
  }

  public getYoutubeId(url: string, opts = { fuzzy: true }): string | undefined {
    if (/youtu\.?be/.test(url)) {
      // Look first for known patterns
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ];

      // If any pattern matches, return the ID
      for (const pattern of patterns) {
        if (pattern.test(url)) {
          const patternResult = pattern.exec(url);
          if (isDefined(patternResult) && isDefined(patternResult[1])) {
            return patternResult[1];
          }
        }
      }

      if (opts.fuzzy) {
        // If that fails, break it apart by certain characters and look
        // for the 11 character key
        const tokens = url.split(/[/&?=#.\s]/g);
        for (const token of tokens) {
          if (/^[^#&?]{11}$/.test(token)) {
            return token;
          }
        }
      }
    }

    return undefined;
  }

  private embedYouTubeImage(id: string, options?: any): any {
    if (typeof options === 'function') {
      options = {};
    }
    options = options || {};

    options.image = this.validYouTubeOptions.indexOf(options.image) > 0 ? options.image : 'default';

    const src = `https://img.youtube.com/vi/${id}/${options.image}.jpg`;

    const result = {
      link: src,
      html: `<img src="${src}"/>`
    };

    return Promise.resolve(result);
  }

  private embedVimeoImage(id: string, options?: any): any {
    if (typeof options === 'function') {
      options = {};
    }

    options = options || {};

    options.image = this.validVimeoOptions.indexOf(options.image) >= 0 ? options.image : 'thumbnail_large';

    return firstValueFrom(this.http.get(`https://vimeo.com/api/v2/video/${id}.json`)
      .pipe(
        map((res: any) => {
          return {
            link: res[0][options.image],
            html: `<img src="${res[0][options.image]}"/>`
          };
        })
      ));
  }

  private embedDailyMotionImage(id: string, options?: any): any {
    if (typeof options === 'function') {
      options = {};
    }

    options = options || {};

    options.image = this.validDailyMotionOptions.indexOf(options.image) >= 0 ? options.image : 'thumbnail_480_url';

    return firstValueFrom(this.http.get(`https://api.dailymotion.com/video/${id}?fields=${options.image}`)
      .pipe(
        map((res: any) => {
          return {
            link: res[options.image],
            html: `<img src="${res[options.image]}"/>`
          };
        })
      ));
  }

  private parseOptions(options: any): any {
    let queryString = '';
    let attributes = '';

    if (options && options.hasOwnProperty('query')) { // eslint-disable-line no-prototype-builtins
      queryString = `?${this.serializeQuery(options.query)}`;
    }

    if (options && options.hasOwnProperty('attr')) { // eslint-disable-line no-prototype-builtins
      const temp = [] as any;

      Object.keys(options.attr).forEach((key) => {
        temp.push(`${key}="${options.attr[key]}"`);
      });

      attributes = ` ${temp.join(' ')}`;
    }
    return {
      query: queryString,
      attr: attributes
    };
  }

  private serializeQuery(query: any): any {
    const queryString: any = [];

    for (const p in query) {
      if (query.hasOwnProperty(p)) { // eslint-disable-line no-prototype-builtins
        queryString.push(`${encodeURIComponent(p)}=${encodeURIComponent(query[p])}`);
      }
    }

    return queryString.join('&');
  }

  private sanitizeIframe(iframe: string): any {
    return this.sanitizer.bypassSecurityTrustHtml(iframe);
  }

  private detectVimeo(url: any): string {
    this.isVimeoOnline().subscribe();

    return (url.hostname === 'vimeo.com') ? url.pathname.split('/')[1] : null;
  }

  private detectYoutube(url: any): string {
    if (url.hostname.indexOf('youtube.com') > -1) {
      return url.search.split('=')[1];
    }

    if (url.hostname === 'youtu.be') {
      return url.pathname.split('/')[1];
    }

    return '';
  }

  private detectDailymotion(url: any): string {
    if (url.hostname.indexOf('dailymotion.com') > -1) {
      return url.pathname.split('/')[2].split('_')[0];
    }

    if (url.hostname === 'dai.ly') {
      return url.pathname.split('/')[1];
    }

    return '';
  }

}
